import { computed, ref, watch, type ComputedRef, type Ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { type TimeAggregation, getSmartTimeAggregation } from '@/types/customCharts';

interface TimeAggregationOption {
  value: TimeAggregation;
  label: string;
  group: 'chronological' | 'distribution';
}

interface UseTimeAggregationOptions {
  includeDistributions?: boolean;
  distributions?: TimeAggregation[];
}

export function useTimeAggregation(
  startDate: Ref<Date>,
  endDate: Ref<Date>,
  options?: UseTimeAggregationOptions
) {
  const { t } = useI18n();
  const includeDistributions = options?.includeDistributions ?? true;

  const aggregation = ref<TimeAggregation>(
    getSmartTimeAggregation(startDate.value, endDate.value)
  );

  const aggregationOptions: ComputedRef<TimeAggregationOption[]> = computed(() => {
    const diffDays =
      (endDate.value.getTime() - startDate.value.getTime()) / (1000 * 60 * 60 * 24);

    const chronological: TimeAggregation[] = [];

    if (diffDays <= 3) {
      chronological.push('hour', 'day');
    } else if (diffDays <= 28) {
      chronological.push('day', 'week');
    } else if (diffDays <= 90) {
      chronological.push('day', 'week', 'month');
    } else if (diffDays <= 365) {
      chronological.push('day', 'week', 'month');
    } else if (diffDays <= 730) {
      chronological.push('week', 'month', 'year');
    } else {
      chronological.push('month', 'year');
    }

    const result: TimeAggregationOption[] = chronological.map((value) => ({
      value,
      label: t(`time_aggregation.${value}`),
      group: 'chronological' as const,
    }));

    if (includeDistributions) {
      const distributions: TimeAggregation[] = options?.distributions ?? ['hour_distribution', 'weekday'];
      for (const value of distributions) {
        result.push({
          value,
          label: t(`time_aggregation.${value}`),
          group: 'distribution' as const,
        });
      }
    }

    return result;
  });

  const isDistributionMode: ComputedRef<boolean> = computed(() => {
    return (
      aggregation.value === 'hour_distribution' ||
      aggregation.value === 'weekday' ||
      aggregation.value === 'month_day'
    );
  });

  function formatXLabel(dateString: string): string {
    const agg = aggregation.value;

    // Distribution modes — API returns raw values, not ISO dates
    if (agg === 'hour_distribution') {
      // API returns "0", "1", ..., "23" or a date string
      const hour = parseInt(dateString, 10);
      if (!isNaN(hour)) return `${hour}h`;
      const d = new Date(dateString);
      return isNaN(d.getTime()) ? dateString : `${d.getHours()}h`;
    }

    if (agg === 'weekday') {
      // API may return day index (1=Mon..7=Sun) or a date string
      const dayIndex = parseInt(dateString, 10);
      if (!isNaN(dayIndex) && dayIndex >= 1 && dayIndex <= 7) {
        // ISO weekday: 1=Monday. Create a known Monday and offset.
        const monday = new Date('2024-01-01'); // Known Monday
        const d = new Date(monday);
        d.setDate(monday.getDate() + (dayIndex - 1));
        return new Intl.DateTimeFormat(undefined, { weekday: 'short' }).format(d);
      }
      const d = new Date(dateString);
      if (!isNaN(d.getTime())) {
        return new Intl.DateTimeFormat(undefined, { weekday: 'short' }).format(d);
      }
      return dateString;
    }

    if (agg === 'month_day') {
      // API returns "1", "2", ..., "31" or a date string
      const day = parseInt(dateString, 10);
      if (!isNaN(day) && day >= 1 && day <= 31) return String(day);
      const d = new Date(dateString);
      return isNaN(d.getTime()) ? dateString : String(d.getDate());
    }

    // Week mode — API returns "YYYY-WW" (e.g., "2026-09" for week 9)
    if (agg === 'week') {
      const weekMatch = dateString.match(/^(\d{4})-(\d{1,2})$/);
      if (weekMatch) {
        return `S${parseInt(weekMatch[2], 10)}`;
      }
      // Falls through to date parsing if it's a full date
    }

    // Month mode — API may return "YYYY-MM" format
    if (agg === 'month') {
      const monthMatch = dateString.match(/^(\d{4})-(\d{2})$/);
      if (monthMatch) {
        // Parse as first day of month
        const d = new Date(`${dateString}-01`);
        if (!isNaN(d.getTime())) {
          return new Intl.DateTimeFormat(undefined, { month: 'short' }).format(d);
        }
      }
    }

    // Year mode — API may return just "YYYY"
    if (agg === 'year' && /^\d{4}$/.test(dateString)) {
      return dateString;
    }

    // Standard date parsing for chronological modes
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return dateString;

    const optionsMap: Record<string, Intl.DateTimeFormatOptions> = {
      hour: { hour: '2-digit' },
      day: { day: 'numeric', month: 'short' },
      week: { day: 'numeric', month: 'short' },
      month: { month: 'short' },
      year: { year: 'numeric' },
    };

    const formatOptions = optionsMap[agg];
    if (formatOptions) {
      return new Intl.DateTimeFormat(undefined, formatOptions).format(d);
    }

    return dateString;
  }

  // Reset aggregation when dates change only if the current value is no longer valid
  watch([startDate, endDate], () => {
    const validValues = aggregationOptions.value.map(o => o.value);
    if (!validValues.includes(aggregation.value)) {
      aggregation.value = getSmartTimeAggregation(startDate.value, endDate.value);
    }
  });

  return {
    aggregation,
    aggregationOptions,
    isDistributionMode,
    formatXLabel,
  };
}
